import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import { yupResolver } from "@hookform/resolvers/yup";
import { useDispatch, useSelector } from "react-redux";
import { useForm } from "react-hook-form";
import { ManualForm } from "./ManualForm";
import { schema } from "./schema";
import {
  getVehicleLookupMake,
  getVehicleLookupModels,
  getVehicleLookupFuelTypes,
  getVehicleLookupProductionYears,
  getVehicleLookupDerivatives,
  appraisalSlice,
  postManualVehicle
} from "../../store/slices/Appraisal";
import { getAppraisalData, selectAppraisal } from "../../store/selectors";
import { colours } from "../../data/colours";
import { clearState, convertToBool, HPIRegCheck } from "../../pages/helpers";
import { useNavigate } from "react-router-dom";

const ManualFormContainer = ({ setRegRequired, setRegFormatIncorrect }) => {
  const {
    register,
    handleSubmit,
    formState: { errors }
  } = useForm({
    resolver: yupResolver(schema)
  });
  const { appraisalSessionId } = useSelector(getAppraisalData);

  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { vehicleLookup, loading, registrationInput } =
    useSelector(selectAppraisal);
  const [make, setMake] = useState(null);
  const [model, setModel] = useState(null);
  const [fuelType, setFuelType] = useState(null);

  const onSubmit = (data) => {
    const {
      chooseYourCar: capVehicleId,
      colour,
      metallicPaint,
      previousOwners: previousKeepers,
      yearOfRegistration
    } = data;

    const payload = {
      capVehicleId: parseInt(capVehicleId),
      registration: registrationInput,
      colour,
      metallicPaint: convertToBool(metallicPaint),
      previousKeepers: parseInt(previousKeepers),
      dateOfRegistrationYear: parseInt(yearOfRegistration.split(" ")[0]),
      registrationPlate: yearOfRegistration.split(" ")[1]
    };
    if (!registrationInput) {
      setRegRequired(true);
    } else {
      const regWithoutSpaces = registrationInput.replace(/\s/g, "");
      if (HPIRegCheck.test(regWithoutSpaces)) {
        dispatch(postManualVehicle({ appraisalSessionId, payload })).then(
          ({ payload }) => {
            if (!payload.error) {
              dispatch(appraisalSlice.actions.setIsManual());
              navigate("/mileage");
            }
          }
        );
      } else {
        setRegFormatIncorrect(true);
        window.scrollTo(0, 0);
      }
    }
  };

  useEffect(() => {
    if (!appraisalSessionId) {
      navigate("/unauthorised");
    } else {
      const getManufacturers = () =>
        dispatch(getVehicleLookupMake({ appraisalSessionId }));
      if (!vehicleLookup.manufacturers && !loading) {
        getManufacturers();
        clearState(setModel);
        clearState(setFuelType);
      }
    }
  }, [dispatch, vehicleLookup, appraisalSessionId, loading]);

  const clearType = (type) => dispatch(appraisalSlice.actions.clearType(type));
  const getModels = (make) =>
    dispatch(getVehicleLookupModels({ make, appraisalSessionId }));
  const getFuelTypes = (make, model) => {
    const payload = { make, model, appraisalSessionId };
    dispatch(getVehicleLookupFuelTypes(payload));
  };
  const getProductionYears = (fuelType) => {
    const payload = { make, model, fuelType, appraisalSessionId };
    dispatch(getVehicleLookupProductionYears(payload));
  };
  const handleYearOfRegistrationChange = (year) => {
    const dateOfRegistrationYear = year.split(" ")[0];
    const registrationPlate = year.split(" ")[1];
    const payload = {
      make,
      model,
      fuelType,
      dateOfRegistrationYear,
      registrationPlate,
      appraisalSessionId
    };
    dispatch(getVehicleLookupDerivatives(payload));
  };

  const handleMakeChange = (make) => {
    clearType("models");
    clearType("fuelTypes");
    clearType("productionYears");
    clearType("derivatives");
    setMake(make);
    getModels(make);
  };

  const handleModelChange = (model) => {
    clearState(setFuelType);
    clearType("fuelTypes");
    clearType("productionYears");
    clearType("derivatives");
    setModel(model);
    getFuelTypes(make, model);
  };

  const handleFuelTypeChange = (fuelType) => {
    clearType("productionYears");
    clearType("derivatives");
    setFuelType(fuelType);
    getProductionYears(fuelType);
  };

  const clearAll = () => {
    clearType("models");
    clearType("fuelTypes");
    clearType("productionYears");
    clearType("derivatives");
  };

  useEffect(() => {
    if (!make) {
      clearAll();
    }
    // eslint-disable-next-line
  }, [make]);

  return (
    <ManualForm
      register={register}
      handleSubmit={handleSubmit}
      errors={errors}
      onSubmit={onSubmit}
      vehicleLookup={vehicleLookup}
      handleMakeChange={handleMakeChange}
      handleModelChange={handleModelChange}
      handleFuelTypeChange={handleFuelTypeChange}
      handleYearOfRegistrationChange={handleYearOfRegistrationChange}
      colours={colours}
    />
  );
};

export default ManualFormContainer;

ManualFormContainer.propTypes = {
  reg: PropTypes.string
};
